import { QUERY_NAME, ROUTES, SERVICES } from "@lhd/constants";
import { handleMessageError } from "@lhd/features/Apps/utils";
import axios from "@lhd/libs/axios";
import axiosV2 from "@lhd/libs/axiosV2";
import {
  EducationWork,
  JobDetail,
  PermissionsCompanyResponse,
  Qualification,
  Worker,
  WorkerDependantName,
  WorkerDependantRelationship,
  WorkerDependantType,
  WorkerInput,
  WorkerPermissionResponse,
  WorkerShareSetting,
  WorkerShareSettingInput,
  WorkerV2,
} from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { notification } from "antd";
import { omit } from "lodash";
import { useNavigate } from "react-router-dom";
import { useReAuthenticationOneTime } from "./auth";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import { AxiosError } from "axios";
import { denormalizeFilterParams } from "./filterKeyMappingUtils";

// Filter options types for v2 API
export interface FilterOptionItem {
  value: string;
  label: string;
  count: number;
}

export interface FilterOptionsResponse {
  success: boolean;
  filter_options: Record<string, FilterOptionItem[]>;
}

// Worker info types
export interface WorkerInfo {
  birthday: string;
  my_number: string;
  worker_no?: string;
  registration_number?: string;
  portrait_image?: File | string | { file_url: string };
  digital_signature?: File | string | { file_url: string };
  current_login_company_id?: number | string;
  is_admin?: boolean;
  can_change_user?: boolean;
  can_change_org_chart?: boolean;
  pension_number?: string | null;
  user?: {
    avatar_original_url: string;
    avatar_thumbnail_url: string;
    signature_original_url: string;
  };
  full_name?: string;
  created_user_login?: boolean;
}

const API_V2 = process.env.REACT_APP_API_URL_V2;

export const useFetchWorkers = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
  companyIdParams?: number,
  isFilterWithoutCompanyId?: boolean,
  getDataMock?: boolean,
  options?: any,
) => {
  const companyId = getCompanyId();

  const denormalizedParams = denormalizeFilterParams(
    filterParams || {},
    "object",
  );

  const requestBody = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    ...denormalizedParams,
    ...(!isFilterWithoutCompanyId && {
      q: {
        company_id_eq: companyIdParams || companyId,
      },
    }),
  };

  return useQuery(
    [
      QUERY_NAME.WORKERS,
      companyId,
      companyIdParams,
      page,
      pageSize,
      filterParams,
    ],
    () => {
      return axiosInstanceV2({
        method: "post",
        url: `${SERVICES.WORKERS}/for_page_workers`,
        data: requestBody,
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      // cacheTime: Infinity,
      // staleTime: Infinity,
      //refetchOnMount: true,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useFetchWorkersWithNoFilter = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
  companyIdParams?: number,
  isFilterWithoutCompanyId?: boolean,
  options?: any,
) => {
  const companyId = getCompanyId();

  const denormalizedParams = denormalizeFilterParams(filterParams || {});

  const requestBody = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    ...denormalizedParams,
    ...(!isFilterWithoutCompanyId && {
      q: {
        company_id_eq: companyIdParams || companyId,
      },
    }),
  };

  return useQuery(
    [
      QUERY_NAME.WORKERS + "_no-filter",
      companyId,
      companyIdParams,
      page,
      pageSize,
      filterParams,
    ],
    () => {
      return axios({
        method: "post",
        baseURL: API_V2,
        url: `${SERVICES.WORKERS}/worker_list_no_permission`,
        data: requestBody,
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
      ...options,
    },
  );
};

export const useFetchWorkersNoCache = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
  companyIdParams?: number,
  isFilterWithoutCompanyId?: boolean,
  options?: any,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    ...filterParams,
    "q[company_id_eq]": companyIdParams || companyId,
  };

  return useQuery(
    [
      QUERY_NAME.WORKERS + "_nocache",
      companyId,
      companyIdParams,
      page,
      pageSize,
      filterParams,
    ],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.WORKERS}/worker_list_no_permission`,
        params: isFilterWithoutCompanyId
          ? omit(params, "q[company_id_eq]")
          : params,
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 0,
      staleTime: 0,
      ...options,
    },
  );
};

export const useFetchOneWorker = (workerId: any) => {
  const { handleAuthen } = useReAuthenticationOneTime(false);
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.WORKER, workerId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.WORKERS}/${workerId}`,
      }).then((res) => res.data as Worker),
    {
      // cacheTime: Infinity,
      // staleTime: Infinity,
      enabled: !!workerId,
      retry: 2,
      onError(e: any) {
        if (e?.response?.status === 422) {
          handleAuthen(companyId);
        }
      },
    },
  );
};

export const useDeleteWorker = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ workerId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.WORKERS}/${workerId}`,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.WORKERS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleWorker = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ listWorkerIds }: any) => {
      const formData = new FormData();
      listWorkerIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      formData.append("company_id", companyId);
      formData.append("login_company_id", companyId);
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.WORKERS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          queryClient.invalidateQueries([QUERY_NAME.WORKERS]);

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.invalidateQueries([QUERY_NAME.WORKERS]);
          notification["success"]({
            message: "保存しました",
          });
        }
      },
    },
  );
};

export const useSubmitWorker = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, workerId }: any) =>
      axios({
        method: workerId ? "put" : "post",
        url: workerId ? `${SERVICES.WORKERS}/${workerId}` : SERVICES.WORKERS,
        data: { ...payload, company_id: getCompanyId() },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.WORKERS]);
        queryClient.invalidateQueries([QUERY_NAME.WORKER]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSubmitSaveWorkers = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.WORKERS_SAVE,
        data: { ...payload, login_company_id: Number(companyId) },
      }),
    {
      onSuccess(e, variables) {
        queryClient.invalidateQueries([QUERY_NAME.WORKERS]);

        const workerIds: string[] =
          variables?.payload?.workers_data
            ?.map((item: any) => item?.id)
            ?.filter((id: any) => id !== null && id !== undefined)
            ?.map((id: any) => String(id)) || [];

        if (workerIds.length > 0) {
          const workerIdSet = new Set(workerIds);
          queryClient.invalidateQueries({
            predicate: (query) => {
              if (!Array.isArray(query.queryKey)) return false;

              return query.queryKey.some((part) => {
                if (part === null || part === undefined) return false;
                if (typeof part === "object") return false;

                return workerIdSet.has(String(part));
              });
            },
          });
        }

        if (e?.data?.errors?.length > 0) {
          const firstError = e.data.errors[0];
          const errorMessage =
            typeof firstError === "string" ? firstError : firstError?.msg;
          notification["error"]({
            message:
              typeof errorMessage === "string" && errorMessage.trim()
                ? errorMessage
                : "保存に失敗しました。",
          });
        }
      },
    },
  );
};

export const useGetListWorkerByOrg = (
  orgNodeId?: string,
  positionIds?: string,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.LESSON_LIST, companyId, orgNodeId, positionIds],
    async () => {
      const response = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-workers`,
        params: {
          company_id: companyId,
          page: 1,
          per_page: 1000,
          ...(orgNodeId && { org_node_ids: orgNodeId }),
          ...(positionIds && { position_id: positionIds }),
        },
      });
      return {
        data: response.data as any,
      };
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useFetchWorkersOnCompany = (
  page?: number,
  pageSize?: number,
  filterParams?: {
    free_workers?: boolean;
    related_to_user_id?: string;
    login_company_id?: string;
  },
) => {
  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    ...filterParams,
  };

  return useQuery(
    [QUERY_NAME.WORKERS + "_no-filter", page, pageSize, filterParams],
    () => {
      return axios({
        method: "get",
        baseURL: API_V2,
        url: `${SERVICES.WORKER_ON_COMPANY}`,
        params: params,
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};

export const useFetchWorkersWithNoFilterV2 = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
  companyIdParams?: number,
  isFilterWithoutCompanyId?: boolean,
  skipCompanyId?: boolean,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    ...filterParams,
    ...(!skipCompanyId && {
      "q[company_id_eq]": companyIdParams || companyId,
    }),
  };

  return useQuery(
    [
      QUERY_NAME.WORKERS + "_no-filter",
      companyId,
      companyIdParams,
      page,
      pageSize,
      filterParams,
    ],
    () => {
      return axios({
        method: "get",
        baseURL: API_V2,
        url: `${SERVICES.WORKERS}/worker_list_no_permission`,
        params: isFilterWithoutCompanyId
          ? omit(params, "q[company_id_eq]")
          : params,
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};

// Fetch workers from setting_members API (for project manager assignment)
// API endpoint: GET /api/v2/workers/setting_members
// API requires: headers (uid, client, access-token) + params (company_id)
export const useFetchWorkerSettingMembers = () => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.WORKERS + "_setting-members", companyId],
    () => {
      return axiosInstanceV2({
        method: "get",
        url: `${SERVICES.WORKERS}/setting_members`,
        params: {
          company_id: companyId, // Required by backend API
        },
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
    },
  );
};
export const useGetWorkerListLessFields = () => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.LIST_LESS_FIELDS],
    () => {
      return axios({
        method: "get",
        baseURL: API_V2,
        url: `${SERVICES.WORKERS}/${SERVICES.LIST_LESS_FIELDS}`,
        params: {
          page: 1,
          per_page: 10000,
          "q[company_id_eq]": companyId,
          company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};

export const useFetchWorkerPermissionForCurrentUser = (workerId: string) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  return useQuery(
    [QUERY_NAME.WORKER_PERMISSION_FOR_CURRENT_USER, workerId],
    async () => {
      const response = await axios({
        method: "get",
        url: `${SERVICES.WORKER_PERMISSIONS}/${workerId}`,
        params: {
          login_company_id: companyId,
        },
      });
      // const workerView = response?.data?.worker_view || [];
      // const workerViewEdit = response?.data?.worker_view_edit || [];
      // const workerViewEditDelete =
      //   response?.data?.worker_view_edit_delete || [];
      // if (
      //   [...workerView, ...workerViewEdit, ...workerViewEditDelete]?.length ===
      //     0 &&
      //   !isRedirect
      // ) {
      //   navigate(`/${ROUTES.NOT_FOUND_PAGE}`);
      // }

      return response.data as WorkerPermissionResponse;
    },
    {
      enabled: !!workerId,
    },
  );
};

export const useFetchWorkerBreadcrumbInfo = (
  id: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<WorkerInfo, Error> => {
  const companyId = getCompanyId();
  // const navigate = useNavigate();
  return useQuery(
    [QUERY_NAME.WORKER_BREADCRUMB_INFO, id],
    async () => {
      const response = await axios({
        method: "get",
        baseURL: API_V2,
        url: `/workers/${id}/breadcrumb_info`,
        params: {
          current_login_company_id: companyId,
        },
      });
      return response.data;
    },
    {
      enabled: !!id && enabled,
      retry: 2,
      // onError(error: any) {
      //   if (
      //     error?.response?.status === 404 ||
      //     error?.response?.status === 403
      //   ) {
      //     navigate(`/${ROUTES.NOT_FOUND_PAGE}`);
      //   }
      // },
    },
  );
};

//update worker info
export const useFetchWorkerInfo = (
  id: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<WorkerInfo, Error> => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  return useQuery(
    [QUERY_NAME.WORKER, id],
    async () => {
      const response = await axios({
        method: "get",
        baseURL: API_V2,
        url: `/workers/${id}`,
        params: {
          current_login_company_id: companyId,
        },
      });
      return response.data;
    },
    {
      enabled: !!id && enabled,
      retry: 2,
      onError(error: any) {
        if (
          error?.response?.status === 404 ||
          error?.response?.status === 403
        ) {
          navigate(`/${ROUTES.NOT_FOUND_PAGE}`);
        }
      },
    },
  );
};

export const useUpdateWorkerInfo = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: Partial<WorkerInfo>) => {
      const formData = new FormData();

      // Add text fields
      formData.append("birthday", payload.birthday ?? "");
      formData.append("my_number", payload.my_number ?? "");
      formData.append("registration_number", payload.registration_number ?? "");
      formData.append("pension_number", payload.pension_number ?? "");

      // Add files
      if (payload.portrait_image) {
        formData.append("portrait_image", payload.portrait_image as File);
      }
      if (payload.digital_signature) {
        formData.append("digital_signature", payload.digital_signature as File);
      }

      if (payload?.current_login_company_id) {
        formData.append(
          "current_login_company_id",
          payload.current_login_company_id.toString(),
        );
      }

      return axiosV2({
        method: "patch",
        url: `/workers/${id}`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.WORKER, id]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        notification["error"]({
          message: "エラー",
          description: error?.response?.data?.message || "更新に失敗しました",
        });
      },
    },
  );
};

export const useFetchWorkerLoginInfo = (
  id: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<WorkerInfo, Error> => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.WORKER_LOGIN_INFO, id],
    async () => {
      const response = await axiosV2({
        method: "get",
        url: `/workers/${id}/login_information`,
        params: {
          current_login_company_id: companyId,
        },
      });
      return response.data;
    },
    {
      enabled: !!id && enabled,
    },
  );
};

export const useUpdateWorkerLoginInfo = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: Partial<WorkerInfo>) => {
      const formData = new FormData();
      formData.append("is_admin", payload.is_admin?.toString() || "false");
      formData.append(
        "can_change_user",
        payload.can_change_user?.toString() || "false",
      );
      formData.append(
        "can_change_org_chart",
        payload.can_change_org_chart?.toString() || "false",
      );

      if (payload?.current_login_company_id) {
        formData.append(
          "current_login_company_id",
          payload.current_login_company_id.toString(),
        );
      }

      return axiosV2({
        method: "patch",
        url: `/workers/${id}/login_information`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.WORKER_LOGIN_INFO, id]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        notification["error"]({
          message: "エラー",
          description: error?.response?.data?.message || "更新に失敗しました",
        });
      },
    },
  );
};

export const useDeleteWorkersEmergencyAddress = (
  id: string,
  { onSuccess, onError }: { onSuccess: () => void; onError: () => void },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: any) => {
      const { recordId } = payload;
      return axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_emergency_addresses/${recordId}`,
        data: {
          current_login_company_id: payload.current_login_company_id,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.EMERGENCY_ADDRESS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError();
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useSubmitWorkersEmergencyAddress = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: any) =>
      axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_emergency_addresses`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.EMERGENCY_ADDRESS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersEmergencyAddress = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_emergency_addresses/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.EMERGENCY_ADDRESS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

// Submit LoginInfo
export const useSubmitWorkersLoginInfo = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/user_accounts`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_LOGIN_INFO,
          companyId,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.WORKER, id]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};
export const useUpdateWorkersLoginInfo = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/user_accounts/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_LOGIN_INFO,
          companyId,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.WORKER, id]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};
// Delete LoginInfo
export const useDeleteWorkersLoginInfo = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/user_accounts/${payload.recordId}`,
        data: {
          current_login_company_id: payload.current_login_company_id,
        },
      }),

    {
      onSuccess() {
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_LOGIN_INFO,
          companyId,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.WORKER, id]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//name history service================
export const useUpdateWorkersNameHistory = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_names/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_NAME_HISTORY,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useSubmitWorkersNameHistory = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_names`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_NAME_HISTORY,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersNameHistory = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_names/${payload.recordId}`,
        data: {
          current_login_company_id: payload.current_login_company_id,
        },
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_NAME_HISTORY,
          companyId,
        ]);
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//bank info service
export const useSubmitWorkersBankInfo = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_bank_infos`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.BANK_INFOS, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};
export const useUpdateWorkersBankInfo = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_bank_infos/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.BANK_INFOS, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};
export const useDeleteWorkersBankInfo = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess?: () => void; onError?: (error: any) => void },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: any) =>
      axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_bank_infos/${payload.recordId}`,
        params: {
          current_login_company_id: payload.current_login_company_id,
        },
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_BANK_INFOS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//phone service
export const useSubmitWorkersPhone = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      return axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_phone_numbers`,
        data: payload,
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_PHONE_NUMBERS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersPhone = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_phone_numbers/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_PHONE_NUMBERS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersPhone = (
  id: string,
  { onSuccess, onError }: { onSuccess: () => void; onError: () => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      const { recordId, ...rest } = payload;
      return axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_phone_numbers/${recordId}`,
        data: {
          ...rest,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_PHONE_NUMBERS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError();
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//gender service
export const useSubmitWorkersGender = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      return axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_genders`,
        data: payload,
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_GENDER, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersGender = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      return axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_genders/${payload.recordId}`,
        data: payload,
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_GENDER, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersGender = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      return axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_genders/${payload.recordId}`,
        params: {
          current_login_company_id: getCompanyId(),
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_GENDER, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};
//current address service
export const useSubmitWorkersCurrentAddress = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_addresses`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_CURRENT_ADDRESS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersCurrentAddress = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_addresses/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_CURRENT_ADDRESS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersCurrentAddress = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      const { recordId, ...rest } = payload;
      return axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_addresses/${recordId}`,
        data: {
          ...rest,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_CURRENT_ADDRESS,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//emergency address contact service
export const useSubmitWorkersEmergencyAddressContact = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_emergency_contacts`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_EMERGENCY_CONTACT,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersEmergencyAddressContact = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_emergency_contacts/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_EMERGENCY_CONTACT,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersEmergencyAddressContact = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_emergency_contacts/${payload.recordId}`,
        data: {
          current_login_company_id: payload.current_login_company_id,
        },
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_EMERGENCY_CONTACT,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//worker_stores service
export const useSubmitWorkersShopName = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/worker_stores`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_STORE_NAMES,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersShopName = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/worker_stores/${payload.recordId}`,
        data: payload,
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_STORE_NAMES,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersShopName = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/worker_stores/${payload.recordId}`,
        data: {
          current_login_company_id: payload.current_login_company_id,
        },
      }),
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_STORE_NAMES,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useSubmitWorkerRelations = () => {
  const companyId = getCompanyId();
  // const navigate = useNavigate();

  return useMutation(
    (data: any) => {
      return axiosV2({
        method: "post",
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        url: SERVICES.WORKERS_RELATIONS,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        // if (e?.data?.errors?.length) {
        //   const msgError = e?.data?.errors;

        //   notification["error"]({
        //     message: msgError?.[0]?.msg || "保存に失敗しました。",
        //   });
        // } else {
        notification["success"]({
          message: "保存しました",
        });
        // navigate("/workers");
        // }
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

//worker onboading service
export const useSubmitWorkersOnboarding = (
  id: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess: () => void;
    onError: (error: any) => void;
  },
) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "post",
        url: `worker_onboardings`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_ONBOARDING,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersOnboarding = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "patch",
        url: `worker_onboardings/${payload.id}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_ONBOARDING,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersOnboarding = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "delete",
        url: `worker_onboardings/${payload.id}`,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_ONBOARDING,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//worker insurance service
export const useSubmitWorkersInsurance = (
  id: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess: () => void;
    onError: (error: any) => void;
  },
) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      delete payload.id;
      return axiosV2({
        method: "post",
        url: `worker_insurances`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_INSURANCE, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersInsurance = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "patch",
        url: `worker_insurances/${payload.id}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_INSURANCE, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersInsurance = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "delete",
        url: `worker_insurances/${payload.id}`,
        data: payload,
        params: { current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_INSURANCE, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//

export const useSubmitWorkersAssignment = (
  id: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess: () => void;
    onError: (error: any) => void;
  },
) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "post",
        url: `worker_assignments`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_ASSIGNMENT,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersAssignment = (
  id: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess: () => void;
    onError: (error: any) => void;
  },
) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "patch",
        url: `worker_assignments/${id}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_ASSIGNMENT,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersAssignment = (
  id: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess: () => void;
    onError: (error: any) => void;
  },
) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "delete",
        url: `worker_assignments/${id}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_ASSIGNMENT,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

// woker devices service
export const useSubmitWorkersDevices = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "post",
        url: `worker_devices`,
        data: payload,
        params: { current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_DEVICE, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersDevices = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "patch",
        url: `worker_devices/${payload.id}`,
        data: payload,
        params: { current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_DEVICE, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersDevices = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "delete",
        url: `worker_devices/${payload.id}`,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_DEVICE, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//worker_base_salaries
export const useSubmitWorkersBaseSalaries = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "post",
        url: `worker_base_salaries`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_BASE_SALARY,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersBaseSalaries = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "patch",
        url: `worker_base_salaries/${payload.id}`,
        data: payload,
        params: { current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_BASE_SALARY,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersBaseSalaries = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "delete",
        url: `worker_base_salaries/${payload.id}`,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_BASE_SALARY,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//email company
export const useSubmitWorkerEmailCompany = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "post",
        url: `worker_company_emails`,
        data: payload,
        params: { current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_COMPANY_EMAIL,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkerEmailCompany = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "patch",
        url: `worker_company_emails/${payload.id}`,
        data: payload,
        params: { current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_COMPANY_EMAIL,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkerEmailCompany = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "delete",
        url: `worker_company_emails/${payload.id}`,
        data: { current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_COMPANY_EMAIL,
          companyId,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

//
export const useFetchWorkerEducationWork = (
  worker_id: any,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKER_BACKGROUNDS,
      "education_work",
      companyId,
      worker_id,
      page,
      pageSize,
    ],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKER_BACKGROUNDS}/education_work`,
        params: params,
      }).then((res) => ({
        data: res.data as EducationWork[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
      onError(e: any) {
        e?.response?.data?.msg &&
          notification.error({
            message: e?.response?.data?.msg,
          });
      },
    },
  );
};

export const useSubmitWorkerEducationWork = (worker_id: any) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => {
      const id = data.id; // if have id . it is meaning updated
      return axiosV2({
        method: id ? "put" : "post",
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url:
          SERVICES.WORKER_BACKGROUNDS +
          "/education_work" +
          (id ? `/${id}` : ""),
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "education_work",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e: any) {
        const errMsg = e?.response?.data?.msg;
        notification["error"]({
          message: errMsg || "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerEducationWork = (worker_id: any) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(
    ({ worker_id, id }: any) => {
      return axiosV2({
        method: "delete",
        params: {
          current_login_company_id: companyId,
          worker_id,
        },
        url: SERVICES.WORKER_BACKGROUNDS + "/education_work" + "/" + id,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "education_work",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerQualifications = (
  worker_id: any,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKER_BACKGROUNDS,
      "qualifications",
      companyId,
      worker_id,
      page,
      pageSize,
    ],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKER_BACKGROUNDS}/qualifications`,
        params: params,
      }).then((res) => ({
        data: res.data as Qualification[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};
export const useSubmitWorkerQualifications = (worker_id: any) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: any) => {
      const id = data.id;
      return axiosV2({
        method: id ? "put" : "post",
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url:
          SERVICES.WORKER_BACKGROUNDS +
          "/qualifications" +
          (id ? `/${id}` : ""),
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "qualifications",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e: any) {
        const errMsg = e?.response?.data?.msg;
        notification["error"]({
          message: errMsg || "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerQualifications = (worker_id: any) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(
    ({ worker_id, id }: any) => {
      return axiosV2({
        method: "delete",
        params: {
          current_login_company_id: companyId,
          worker_id,
        },
        url: SERVICES.WORKER_BACKGROUNDS + "/qualifications" + "/" + id,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "qualifications",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerJobDetails = (
  worker_id: any,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKER_BACKGROUNDS,
      "job_details",
      companyId,
      worker_id,
      page,
      pageSize,
    ],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKER_BACKGROUNDS}/job_details`,
        params: params,
      }).then((res) => ({
        data: res.data as JobDetail[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};
export const useSubmitWorkerJobDetails = (worker_id: any) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: any) => {
      const id = data.id;
      return axiosV2({
        method: id ? "put" : "post",
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url:
          SERVICES.WORKER_BACKGROUNDS + "/job_details" + (id ? `/${id}` : ""),
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "job_details",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e: any) {
        const errMsg = e?.response?.data?.msg;
        notification["error"]({
          message: errMsg || "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerJobDetails = (worker_id: any) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    ({ worker_id, id }: any) => {
      return axiosV2({
        method: "delete",
        params: {
          current_login_company_id: companyId,
          worker_id,
        },
        url: SERVICES.WORKER_BACKGROUNDS + "/job_details" + "/" + id,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "job_details",
              companyId,
              worker_id,
            ],
            exact: false,
          });

          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerFileBackground = (worker_id: any) => {
  const companyId = getCompanyId();

  const params = {
    current_login_company_id: companyId,
    worker_id,
    background_type: "general",
  };

  return useQuery(
    [SERVICES.WORKER_BACKGROUNDS, "files", companyId, worker_id],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKER_BACKGROUNDS}/files`,
        params: params,
      }).then((res) => ({
        data: res.data as any[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};

export const useSubmitWorkerFileBackground = (worker_id: any) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: any) => {
      const isUpdate = data.isEdit;
      const url = isUpdate
        ? SERVICES.WORKER_BACKGROUNDS + "/update_files"
        : SERVICES.WORKER_BACKGROUNDS + "/upload_file";
      const method = isUpdate ? "put" : "post";

      return axiosV2({
        method,
        data: {
          current_login_company_id: companyId,
          background_type: "general",
          ...data,
        },
        url,
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "files",
              companyId,
              worker_id,
            ],
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e: AxiosError<{ msg: string }>) {
        e.response?.data?.msg
          ? notification["error"]({
              message: e.response?.data?.msg,
            })
          : notification["error"]({
              message: "保存に失敗しました。",
            });
      },
    },
  );
};

export const useFetchWorkerRegistrationNumber = (
  id: string,
): UseQueryResult<any, Error> => {
  const companyId = getCompanyId();
  return useQuery(
    [SERVICES.WORKER_DEATH_INFO, companyId],
    () => {
      return axiosV2({
        method: "get",
        url: `/workers/${id}/${SERVICES.WORKER_DEATH_INFO}`,
        params: {
          current_login_company_id: companyId,
        },
      }).then((res) => res.data);
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
    },
  );
};

export const useUpdateWorkerDeathInfo = (id: string) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "put",
        url: `/workers/${id}/${SERVICES.WORKER_DEATH_INFO}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess(e) {
        queryClient.refetchQueries({
          queryKey: [SERVICES.WORKER_DEATH_INFO, companyId],
          exact: false,
        });
        notification.success({
          message: "保存しました",
          description: "死亡情報が正常に保存されました。",
        });
      },
      onError(e: AxiosError<{ msg: string }>) {
        e.response?.data?.msg
          ? notification["error"]({
              message: e.response?.data?.msg,
            })
          : notification["error"]({
              message: "保存に失敗しました。",
            });
      },
    },
  );
};

export const useFetchWorkerBasicInformation = (
  id: string,
): UseQueryResult<any> => {
  const companyId = getCompanyId();
  return useQuery(
    [SERVICES.WORKER_BASIC_INFORMATION, companyId],
    () => {
      return axiosV2({
        method: "get",
        url: `${SERVICES.WORKER_BASIC_INFORMATION}/${id}`,
        params: {
          current_login_company_id: companyId,
        },
      }).then((res) => res.data);
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
    },
  );
};
export const useFetchPreviousEmploymentCompanies = (
  id: string,
): UseQueryResult<any> => {
  const companyId = getCompanyId();
  return useQuery(
    [SERVICES.PREVIOUS_EMPLOYMENT_COMPANIES, companyId],
    () => {
      return axiosV2({
        method: "get",
        url: `${SERVICES.PREVIOUS_EMPLOYMENT_COMPANIES}`,
        params: {
          worker_id: id,
          company_id: companyId,
        },
      }).then((res) => res.data);
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
    },
  );
};

export const useUpdatePreviousEmploymentCompanies = () => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (payload: any) => {
      const { worker_id, ...restPayload } = payload;
      return axiosV2({
        method: payload.id ? "patch" : "post",
        url: payload.id
          ? `${SERVICES.PREVIOUS_EMPLOYMENT_COMPANIES}/${payload.id}`
          : `${SERVICES.PREVIOUS_EMPLOYMENT_COMPANIES}`,
        data: restPayload,
        params: { company_id: companyId, worker_id },
      });
    },
    {
      onSuccess(e) {
        queryClient.refetchQueries({
          queryKey: [SERVICES.PREVIOUS_EMPLOYMENT_COMPANIES, companyId],
        });
        notification.success({
          message: "保存しました",
        });
      },
      onError(e: AxiosError<{ msg: string }>) {
        e.response?.data?.msg
          ? notification["error"]({
              message: e.response?.data?.msg,
            })
          : notification["error"]({
              message: "保存に失敗しました。",
            });
      },
    },
  );
};

export const useUpdateWorkerBasicInformation = () => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "patch",
        url: `${SERVICES.WORKER_BASIC_INFORMATION}/${payload.id}`,
        data: { ...payload, current_login_company_id: companyId },
      });
    },
    {
      onSuccess(e) {
        queryClient.refetchQueries({
          queryKey: [SERVICES.WORKER_BASIC_INFORMATION, companyId],
          exact: false,
        });
        notification.success({
          message: "保存しました",
          description: "基本情報が正常に保存されました。",
        });
      },
      onError(e: AxiosError<{ msg: string }>) {
        e.response?.data?.msg
          ? notification["error"]({
              message: e.response?.data?.msg,
            })
          : notification["error"]({
              message: "保存に失敗しました。",
            });
      },
    },
  );
};

export const useFetchWorkerCompanyInfo = (
  worker_id: any,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKER_BACKGROUNDS,
      "education_work",
      companyId,
      worker_id,
      page,
      pageSize,
    ],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKER_BACKGROUNDS}/education_work`,
        params: params,
      }).then((res) => ({
        data: res.data as any[], // CompanyInfo[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};
export const useSubmitWorkerCompanyInfo = (worker_id: any) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => {
      const id = data.id; // if have id . it is meaning updated
      return axiosV2({
        method: id ? "put" : "post",
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url:
          SERVICES.WORKER_BACKGROUNDS +
          "/education_work" +
          (id ? `/${id}` : ""),
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "education_work",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerCompanyInfo = (worker_id: any) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(
    ({ worker_id, id }: any) => {
      return axiosV2({
        method: "delete",
        params: {
          current_login_company_id: companyId,
          worker_id,
        },
        url: SERVICES.WORKER_BACKGROUNDS + "/education_work" + "/" + id,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "education_work",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useGetWorkerPassword = (id: string) => {
  return useMutation(
    (payload: any) =>
      axios({
        method: "get",
        url: `${SERVICES.WORKERS}/${id}/user_accounts/current/password`,
        params: payload,
      }),
    {
      onSuccess(data) {
        return data?.data;
      },
      onError(error) {
        return error;
      },
    },
  );
};

export const useUpdateWorkerPassword = (id: string) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) =>
      axiosV2({
        method: "put",
        url: `${SERVICES.WORKERS}/${id}/user_accounts/current/password`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      }),
    {
      onSuccess(data) {
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_LOGIN_INFO,
          QUERY_NAME.WORKER_PASSWORD,
        ]);
      },
      onError(error: { response: { data: { msg: string } } }) {
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

// Worker Emails service
export const useSubmitWorkersEmails = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      return axios({
        method: "post",
        baseURL: API_V2,
        url: `/workers/${id}/user_worker_emails`,
        data: payload,
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_EMAILS, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateWorkersEmails = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      return axios({
        method: "put",
        baseURL: API_V2,
        url: `/workers/${id}/user_worker_emails/${payload.recordId}`,
        data: payload,
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_EMAILS, companyId]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteWorkersEmails = (
  id: string,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) => {
      return axios({
        method: "delete",
        baseURL: API_V2,
        url: `/workers/${id}/user_worker_emails/${payload.recordId}`,
        data: {
          current_login_company_id: payload.current_login_company_id,
        },
      });
    },
    {
      onSuccess() {
        onSuccess && onSuccess();
        const companyId = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_EMAILS, companyId]);
        notification["success"]({
          message: "削除しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.msg),
        });
      },
    },
  );
};

export const useFetchWorkerOrgChartNodeHistories = (
  worker_id: any,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKER_ORG_CHART_NODE_HISTORIES,
      companyId,
      worker_id,
      page,
      pageSize,
    ],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKER_ORG_CHART_NODE_HISTORIES}`,
        params: params,
      }).then((res) => ({
        data: res.data as any[], // CompanyInfo[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};
export const useSubmitDocuments = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "post",
        url: `${SERVICES.WORKER_DOCUMENTS}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess(data) {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([SERVICES.WORKER_DOCUMENTS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error) {
        onError && onError(error);
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useUpdateDocuments = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const companyId = getCompanyId();
      return axiosV2({
        method: "patch",
        url: `${SERVICES.WORKER_DOCUMENTS}/${payload.recordId}`,
        data: payload?.formData,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess(data) {
        onSuccess && onSuccess();
        queryClient.invalidateQueries([SERVICES.WORKER_DOCUMENTS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error) {
        onError && onError(error);
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useDeleteDocuments = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "delete",
        url: `${SERVICES.WORKER_DOCUMENTS}/${payload.id}`,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess(data) {
        queryClient.invalidateQueries([SERVICES.WORKER_DOCUMENTS]);
        onSuccess && onSuccess();
        notification["success"]({
          message: "削除しました",
        });
      },
      onError(error) {
        onError && onError(error);
        notification["error"]({
          message: "削除に失敗しました。",
        });
      },
    },
  );
};

export const useSubmitWorkersRegistrationCode = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "post",
        url: `${SERVICES.WORKER_REGISTRATION_CODES}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.WORKER_REGISTRATION_CODES]);
        onSuccess && onSuccess();
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error) {
        onError && onError(error);
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useUpdateWorkersRegistrationCode = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "patch",
        url: `${SERVICES.WORKER_REGISTRATION_CODES}/${payload.recordId}`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.WORKER_REGISTRATION_CODES]);
        onSuccess && onSuccess();
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error) {
        onError && onError(error);
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useDeleteWorkersRegistrationCode = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      return axiosV2({
        method: "delete",
        url: `${SERVICES.WORKER_REGISTRATION_CODES}/${payload.recordId}`,
        params: { ...payload, current_login_company_id: companyId },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.WORKER_REGISTRATION_CODES]);
        onSuccess && onSuccess();
        notification["success"]({
          message: "削除しました",
        });
      },
      onError(error) {
        onError && onError(error);
        notification["error"]({
          message: "削除に失敗しました。",
        });
      },
    },
  );
};

// worker_dependants

export const useUpdateCreateWorkerDependants = (workerId: string) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (payload: any) => {
      const isEdit = !!payload.id;
      return axiosV2({
        method: isEdit ? "put" : "post",
        url: `${SERVICES.WORKERS}/${workerId}${SERVICES.WORKER_DEPENDANTS}${
          isEdit ? `/${payload.id}` : ""
        }`,
        data: payload,
        params: {
          current_login_company_id: companyId,
        },
      });
    },
    {
      onSuccess(data) {
        queryClient.invalidateQueries([SERVICES.WORKER_DEPENDANTS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerDependants = (workerId: string) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();

  const params = {
    current_login_company_id: companyId,
  };

  return useQuery(
    [SERVICES.WORKER_DEPENDANTS, companyId],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKERS}/${workerId}/${SERVICES.WORKER_DEPENDANTS}`,
        params: params,
      }).then((res) => ({
        data: res.data as WorkerDependantType[], // CompanyInfo[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },

    {
      cacheTime: 1000,
      staleTime: 1000,
      onError(e: any) {
        notification.error({ message: e?.response?.data?.msg });
        if (e.response.status === 404) {
          navigate(SERVICES.WORKERS);
        }
      },
      //refetchOnMount: true,
    },
  );
};

export const useDeleteWorkersDependants = (workerId: string) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  const params = {
    current_login_company_id: companyId,
  };
  return useMutation(
    (payload: { dependant_ids: string[] }) => {
      return axiosV2({
        method: "delete",
        url: `${SERVICES.WORKERS}/${workerId}${SERVICES.WORKER_DEPENDANTS}/batch_delete`,
        params: params,
        data: payload,
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.WORKER_DEPENDANTS, companyId]);
        notification["success"]({
          message: "削除しました",
        });
      },
      onError(error) {
        notification["error"]({
          message: "削除に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerDependantGenders = (
  worker_id: any,
  isNew: boolean,
  page?: number,
  pageSize?: number,
  dependant_id?: any,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKERS,
      worker_id,
      SERVICES.WORKER_DEPENDANTS,
      "worker_dependant_genders",
      companyId,
      dependant_id,
      pageSize,
      page,
    ],
    () => {
      if (!isNew) {
        return axiosV2({
          url: `${SERVICES.WORKERS}/${worker_id}/${SERVICES.WORKER_DEPENDANTS}/${dependant_id}/worker_dependant_genders`,
          params: params,
        }).then((res) => ({
          data: res.data as any[], // CompanyInfo[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        }));
      }
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};
export const useSubmitWorkerDependantGenders = (
  worker_id: any,
  dependant_id: any,
) => {
  const companyId = getCompanyId();

  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => {
      const id = data.id; // if have id . it is meaning updated
      return axiosV2({
        method: id ? "put" : "post",
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url: `${SERVICES.WORKERS}/${worker_id}/${SERVICES.WORKER_DEPENDANTS}/${
          data.dependant_id
        }/worker_dependant_genders${id ? `/${id}` : ""}`,
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKERS,
              worker_id,
              SERVICES.WORKER_DEPENDANTS,
              "worker_dependant_genders",
              companyId,
              dependant_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerDependantGender = (
  worker_id: any,
  dependant_id: any,
) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    ({ dependant_id, gender_id }: any) => {
      return axiosV2({
        method: "delete",
        params: {
          current_login_company_id: companyId,
        },
        url: `${SERVICES.WORKERS}/${worker_id}/${SERVICES.WORKER_DEPENDANTS}/${dependant_id}/worker_dependant_genders/${gender_id}`,
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "削除に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKERS,
              worker_id,
              SERVICES.WORKER_DEPENDANTS,
              "worker_dependant_genders",
              companyId,
              dependant_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "削除しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "削除に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerDependantNames = (
  worker_id: string,
  dependant_id: string,
  isNew: boolean,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKERS,
      worker_id,
      SERVICES.WORKER_DEPENDANTS,
      dependant_id,
      SERVICES.WORKER_DEPENDANT_NAMES,
      companyId,
      pageSize,
      page,
    ],
    () => {
      if (!isNew) {
        return axiosV2({
          url: `${SERVICES.WORKERS}/${worker_id}${SERVICES.WORKER_DEPENDANTS}/${dependant_id}${SERVICES.WORKER_DEPENDANT_NAMES}`,
          params: params,
        }).then((res) => ({
          data: res.data as WorkerDependantName[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        }));
      }
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};
export const useSubmitWorkerDependantNames = (
  worker_id: string,
  dependant_id: string,
) => {
  const companyId = getCompanyId();

  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => {
      const id = data.id;
      return axios({
        method: id ? "put" : "post",
        baseURL: API_V2,
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url: `${SERVICES.WORKERS}/${worker_id}${
          SERVICES.WORKER_DEPENDANTS
        }/${dependant_id}${SERVICES.WORKER_DEPENDANT_NAMES}/${id ? id : ""}`,
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKERS,
              worker_id,
              SERVICES.WORKER_DEPENDANTS,
              dependant_id,
              SERVICES.WORKER_DEPENDANT_NAMES,
              companyId,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerDependantNames = (
  worker_id: string,
  dependant_id: string,
) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (name_id: number | string) => {
      return axios({
        method: "delete",
        params: {
          current_login_company_id: companyId,
          worker_id,
        },
        url: `${SERVICES.WORKERS}/${worker_id}${SERVICES.WORKER_DEPENDANTS}/${dependant_id}${SERVICES.WORKER_DEPENDANT_NAMES}/${name_id}`,
        baseURL: API_V2,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "削除に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKERS,
              worker_id,
              SERVICES.WORKER_DEPENDANTS,
              dependant_id,
              SERVICES.WORKER_DEPENDANT_NAMES,
              companyId,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerDependantAddresses = (
  worker_id: any,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKER_BACKGROUNDS,
      "education_work",
      companyId,
      worker_id,
      page,
      pageSize,
    ],
    () => {
      return axiosV2({
        url: `${SERVICES.WORKER_BACKGROUNDS}/education_work`,
        params: params,
      }).then((res) => ({
        data: res.data as any[], // CompanyInfo[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
      //refetchOnMount: true,
    },
  );
};
export const useSubmitWorkerDependantAddresses = (worker_id: any) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => {
      const id = data.id; // if have id . it is meaning updated
      return axiosV2({
        method: id ? "put" : "post",
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url:
          SERVICES.WORKER_BACKGROUNDS +
          "/education_work" +
          (id ? `/${id}` : ""),
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "education_work",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerDependantAddresses = (worker_id: any) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(
    ({ worker_id, id }: any) => {
      return axiosV2({
        method: "delete",
        params: {
          current_login_company_id: companyId,
          worker_id,
        },
        url: SERVICES.WORKER_BACKGROUNDS + "/education_work" + "/" + id,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKER_BACKGROUNDS,
              "education_work",
              companyId,
              worker_id,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerDependantRelationships = (
  worker_id: string,
  dependant_id: string,
  isNew: boolean,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();

  const params = {
    ...(pageSize !== 0 && {
      page,
      per_page: pageSize,
    }),
    current_login_company_id: companyId,
    worker_id,
  };

  return useQuery(
    [
      SERVICES.WORKERS,
      worker_id,
      SERVICES.WORKER_DEPENDANTS,
      dependant_id,
      SERVICES.WORKER_DEPENDANT_RELATIONSHIPS,
      companyId,
      pageSize,
      page,
    ],
    () => {
      if (!isNew) {
        return axiosV2({
          url: `${SERVICES.WORKERS}/${worker_id}${SERVICES.WORKER_DEPENDANTS}/${dependant_id}${SERVICES.WORKER_DEPENDANT_RELATIONSHIPS}`,
          params: params,
        }).then((res) => ({
          data: res.data as WorkerDependantRelationship[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        }));
      }
    },
    {
      cacheTime: 1000,
      staleTime: 1000,
    },
  );
};
export const useSubmitWorkerDependantRelationships = (
  worker_id: string,
  dependant_id: string,
) => {
  const companyId = getCompanyId();

  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => {
      const id = data.id;
      return axios({
        method: id ? "put" : "post",
        baseURL: API_V2,
        data: {
          current_login_company_id: companyId,
          ...data,
        },
        params: {
          page: data.page,
          per_page: data.per_page,
        },
        url: `${SERVICES.WORKERS}/${worker_id}${
          SERVICES.WORKER_DEPENDANTS
        }/${dependant_id}${SERVICES.WORKER_DEPENDANT_RELATIONSHIPS}/${
          id ? id : ""
        }`,
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKERS,
              worker_id,
              SERVICES.WORKER_DEPENDANTS,
              dependant_id,
              SERVICES.WORKER_DEPENDANT_RELATIONSHIPS,
              companyId,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};
export const useDeleteWorkerDependantRelationships = (
  worker_id: string,
  dependant_id: string,
) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (name_id: number | string) => {
      return axios({
        method: "delete",
        params: {
          current_login_company_id: companyId,
          worker_id,
        },
        url: `${SERVICES.WORKERS}/${worker_id}${SERVICES.WORKER_DEPENDANTS}/${dependant_id}/dependant_relationships/${name_id}`,
        baseURL: API_V2,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;

          notification["error"]({
            message: msgError?.[0]?.msg || "削除に失敗しました。",
          });
        } else {
          queryClient.refetchQueries({
            queryKey: [
              SERVICES.WORKERS,
              worker_id,
              SERVICES.WORKER_DEPENDANTS,
              dependant_id,
              SERVICES.WORKER_DEPENDANT_RELATIONSHIPS,
              companyId,
            ],
            exact: false,
          });
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useGetCurrentUserPermissions = () => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.COMPANY_PERMISSION_WITH_USER, companyId],
    async () => {
      const response = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/current-user-permissions`,
        params: {
          login_company_id: companyId,
        },
      });
      return {
        data: response.data as PermissionsCompanyResponse,
      };
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useGetWorkerInWorkerPermissions = (
  orgNodeIds?: string,
  positionIds?: string,
  options?: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [
      QUERY_NAME.WORKERS_IN_WORKER_PERMISSIONS,
      companyId,
      orgNodeIds,
      positionIds,
    ],
    async () => {
      const response = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-workers`,
        params: {
          company_id: companyId,
          page: 1,
          per_page: 10000,
          ...(orgNodeIds && { org_node_ids: orgNodeIds }),
          ...(positionIds && { position_ids: positionIds }),
        },
      });
      return {
        data: response.data as WorkerV2[],
      };
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      ...options,
    },
  );
};

export const useSubmitWorkers = () => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: WorkerInput) => {
      return axiosV2({
        method: "PUT",
        data: {
          login_company_id: companyId,
          workers_data: data,
        },
        url: `${SERVICES.WORKERS}/bulk_upsert_worker`,
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.WORKERS]);
      },
    },
  );
};

export const useGetWorkerShareSettings = (worker_id: string | number) => {
  return useQuery(
    [QUERY_NAME.WORKER_SHARE_SETTINGS, worker_id],
    async () => {
      const response = await axiosInstanceV2({
        method: "GET",
        url: `${SERVICES.WORKER_SHARE_SETTINGS}`,
        params: {
          page: 1,
          per_page: 10000,
          worker_id,
          current_login_company_id: getCompanyId(),
        },
      });
      return {
        data: response.data as WorkerShareSetting[],
      };
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useUpdateWorkerShareSettings = (worker_id: string | number) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: WorkerShareSettingInput[]) => {
      return axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.WORKER_SHARE_SETTINGS}/save`,
        data: {
          worker_id,
          share_settings: data,
        },
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([
          QUERY_NAME.WORKER_SHARE_SETTINGS,
          worker_id,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkerChartOrgs = (
  workerId?: string | number,
  enabled = true,
) => {
  return useQuery(
    ["worker_chart_organizations", workerId],
    () => {
      if (!workerId) return Promise.resolve([]);
      return axiosV2
        .get(`/workers/${workerId}/chart_organizations`)
        .then((res) => res.data || []);
    },
    {
      enabled: !!workerId && enabled,
      staleTime: 5000,
    },
  );
};

// New API v2: Filter options with cascading filters
export interface FetchWorkerFilterOptionsParams {
  company_id: number | string;
  target_tab?: string;
  app_type?: string;
  field_key: string;
  active_filters: Record<string, string[]>;
  filter_order: string[];
  filter_status?: string[];
  filter_category?: string[];
  worker_ids?: number[];
  has_appraisal_sheet?: boolean;
}

export const useFetchWorkerFilterOptions = (
  params: FetchWorkerFilterOptionsParams | null,
  enabled = true,
) => {
  const queryClient = useQueryClient();

  return useQuery(
    [
      QUERY_NAME.WORKERS,
      "filter_options",
      params?.company_id,
      params?.target_tab || params?.app_type,
      params?.field_key,
      params?.active_filters,
      params?.filter_order,
    ],
    async () => {
      if (!params) return null;

      const res = await axiosInstanceV2({
        method: "post",
        url: `${SERVICES.WORKERS}/for_page_workers/filter_options`,
        data: {
          company_id: params.company_id,
          target_tab: params.target_tab,
          app_type: params.app_type,
          field_key: params.field_key,
          active_filters: params.active_filters,
          filter_order: params.filter_order,
          ...(params.filter_status && { filter_status: params.filter_status }),
          ...(params.filter_category && {
            filter_category: params.filter_category,
          }),
          ...(params.worker_ids && { worker_ids: params.worker_ids }),
          ...(params.has_appraisal_sheet && {
            has_appraisal_sheet: params.has_appraisal_sheet,
          }),
        },
      });

      return res.data as FilterOptionsResponse;
    },
    {
      enabled: !!params && enabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess(result) {
        if (params) {
          queryClient.setQueryData(
            [
              QUERY_NAME.WORKERS,
              "filter_options",
              params.company_id,
              params.target_tab || params.app_type,
              params.field_key,
              params.active_filters,
              params.filter_order,
            ],
            result,
          );
        }
      },
    },
  );
};

export const useFetchWorkerFilterOptionsForAddModal = (
  params: FetchWorkerFilterOptionsParams | null,
  enabled = true,
) => {
  const queryClient = useQueryClient();

  return useQuery(
    [
      QUERY_NAME.WORKERS,
      "filter_options_add_modal",
      params?.company_id,
      params?.target_tab || params?.app_type,
      params?.field_key,
      params?.active_filters,
      params?.filter_order,
    ],
    async () => {
      if (!params) return null;

      const res = await axiosInstanceV2({
        method: "post",
        url: `${SERVICES.WORKERS}/worker_list_no_permission/filter_options`,
        data: {
          company_id: params.company_id,
          target_tab: params.target_tab,
          app_type: params.app_type,
          field_key: params.field_key,
          active_filters: params.active_filters,
          filter_order: params.filter_order,
          ...(params.filter_status && { filter_status: params.filter_status }),
          ...(params.filter_category && {
            filter_category: params.filter_category,
          }),
          ...(params.worker_ids && { worker_ids: params.worker_ids }),
          ...(params.has_appraisal_sheet && {
            has_appraisal_sheet: params.has_appraisal_sheet,
          }),
        },
      });

      return res.data as FilterOptionsResponse;
    },
    {
      enabled: !!params && enabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess(result) {
        if (params) {
          queryClient.setQueryData(
            [
              QUERY_NAME.WORKERS,
              "filter_options_add_modal",
              params.company_id,
              params.target_tab || params.app_type,
              params.field_key,
              params.active_filters,
              params.filter_order,
            ],
            result,
          );
        }
      },
    },
  );
};
